import { isSupportedOrderProductClass } from "@features/orders-features/functions/productQuickView";
import type { OrderDrawerIdentifier } from "./types";

const BI_PREFIX = "bi-";
const WEB_PREFIX = "web-";
const SALES_ORDER_PREFIX = "so-";

// Scoped ids such as `{orderKey}~{shipmentKey}` split at the first `~`.
const SCOPE_SEPARATOR = "~";

const POSITIVE_INTEGER = /^[1-9]\d*$/;
// The product drawer API parses the descriptor as an integer (SHI part number).
const NUMERIC_DESCRIPTOR = /^[0-9]+$/;

const isPositiveInteger = (value: number | null | undefined): value is number =>
  value != null && Number.isSafeInteger(value) && value > 0;

const parsePositiveInteger = (value: string): number | undefined => {
  if (!POSITIVE_INTEGER.test(value)) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : undefined;
};

const isValidSalesOrder = (value: string): boolean =>
  value.length > 0 &&
  value === value.trim() &&
  !value.includes(SCOPE_SEPARATOR);

/**
 * Builds the URL id of the order drawer, e.g. `bi-81234`. Uses the same precedence as the
 * drawer's fetch (bi > web > sales order), so a deep link loads the order the same way an
 * in-app open does. Returns `undefined` when the identifier has no usable id.
 */
export function toOrderRouteKey(
  identifier: OrderDrawerIdentifier,
): string | undefined {
  if (isPositiveInteger(identifier.biOrderId)) {
    return `${BI_PREFIX}${identifier.biOrderId}`;
  }
  if (isPositiveInteger(identifier.webOrderId)) {
    return `${WEB_PREFIX}${identifier.webOrderId}`;
  }
  const salesOrder = identifier.salesOrder?.trim() ?? "";
  return isValidSalesOrder(salesOrder)
    ? `${SALES_ORDER_PREFIX}${salesOrder}`
    : undefined;
}

/** Reverses `toOrderRouteKey`. Returns `undefined` for a key it would not have built. */
export function parseOrderRouteKey(
  key: string,
): OrderDrawerIdentifier | undefined {
  if (key.startsWith(BI_PREFIX)) {
    const biOrderId = parsePositiveInteger(key.slice(BI_PREFIX.length));
    return biOrderId === undefined ? undefined : { salesOrder: "", biOrderId };
  }
  if (key.startsWith(WEB_PREFIX)) {
    const webOrderId = parsePositiveInteger(key.slice(WEB_PREFIX.length));
    return webOrderId === undefined
      ? undefined
      : { salesOrder: "", webOrderId };
  }
  if (key.startsWith(SALES_ORDER_PREFIX)) {
    const salesOrder = key.slice(SALES_ORDER_PREFIX.length);
    return isValidSalesOrder(salesOrder) ? { salesOrder } : undefined;
  }
  return undefined;
}

/**
 * Joins two parts into a scoped route id such as `{orderKey}~{shipmentKey}`. Returns
 * `undefined` when either part is empty or the scope part contains the separator, because
 * `parseScopedRouteId` could not split it back.
 */
export function toScopedRouteId(
  scope: string | null | undefined,
  id: string | null | undefined,
): string | undefined {
  if (!scope || !id || scope.includes(SCOPE_SEPARATOR)) {
    return undefined;
  }
  return `${scope}${SCOPE_SEPARATOR}${id}`;
}

/** Splits a scoped route id at its first `~`. Returns `undefined` when either part is empty. */
export function parseScopedRouteId(
  routeId: string,
): { scope: string; id: string } | undefined {
  const separatorIndex = routeId.indexOf(SCOPE_SEPARATOR);
  if (separatorIndex <= 0 || separatorIndex === routeId.length - 1) {
    return undefined;
  }
  return {
    scope: routeId.slice(0, separatorIndex),
    id: routeId.slice(separatorIndex + 1),
  };
}

/**
 * Hashes a tracking number into an opaque 8-hex-digit key (32-bit FNV-1a of the trimmed
 * value), so a tracking number never appears in the URL. Returns `undefined` for a blank one.
 */
export function getShipmentRouteKey(
  trackingNumber: string | null | undefined,
): string | undefined {
  const trimmed = trackingNumber?.trim();
  if (!trimmed) {
    return undefined;
  }
  let hash = 0x811c9dc5;
  for (let index = 0; index < trimmed.length; index += 1) {
    hash ^= trimmed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

export interface ProductRouteTarget {
  productClass: string;
  productDescriptor: string;
}

/**
 * Builds the URL id of the product drawer, `{productClass}~{productDescriptor}` such as
 * `shicommodity~4567`. Returns `undefined` for a product the drawer API cannot load: an
 * unsupported class or a non-numeric descriptor.
 */
export function toProductRouteId(
  productClass: string | null | undefined,
  productDescriptor: string | null | undefined,
): string | undefined {
  const normalizedClass = productClass?.toLowerCase();
  if (
    !isSupportedOrderProductClass(normalizedClass) ||
    !productDescriptor ||
    !NUMERIC_DESCRIPTOR.test(productDescriptor)
  ) {
    return undefined;
  }
  return toScopedRouteId(normalizedClass, productDescriptor);
}

/** Reverses `toProductRouteId`. Returns `undefined` for an id it would not have built. */
export function parseProductRouteId(
  routeId: string,
): ProductRouteTarget | undefined {
  const parts = parseScopedRouteId(routeId);
  if (!parts || toProductRouteId(parts.scope, parts.id) !== routeId) {
    return undefined;
  }
  return { productClass: parts.scope, productDescriptor: parts.id };
}
